import { APP_BUILD_ID, APP_VERSION } from '../constants';

const BACKUP_FORMAT_VERSION = 1;
const BACKUP_FORMAT = 'rethread_encrypted_backup';
const LEGACY_BACKUP_FORMAT = 'friction_journal_encrypted_backup';
const PBKDF2_ITERATIONS = 210000;
const PBKDF2_ITERATIONS_MIN = 150000;
const PBKDF2_ITERATIONS_MAX = 600000;

type BackupEnvelopeV1 = {
  format: typeof BACKUP_FORMAT | typeof LEGACY_BACKUP_FORMAT;
  version: number;
  appVersion: string;
  appBuildId: string;
  createdAtMs: number;
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  saltB64: string;
  cipher: 'AES-GCM';
  ivB64: string;
  payloadB64: string;
};

export async function createEncryptedBackup(dbBytes: Uint8Array, passphrase: string): Promise<string> {
  const trimmed = passphrase.trim();
  if (trimmed.length < 8) {
    throw new Error('Passphrase must be at least 8 characters.');
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveAesKey(trimmed, salt, PBKDF2_ITERATIONS);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: toOwnedArrayBuffer(iv) }, key, toOwnedArrayBuffer(dbBytes));
  const envelope: BackupEnvelopeV1 = {
    format: BACKUP_FORMAT,
    version: BACKUP_FORMAT_VERSION,
    appVersion: APP_VERSION,
    appBuildId: APP_BUILD_ID,
    createdAtMs: Date.now(),
    kdf: 'PBKDF2-SHA256',
    iterations: PBKDF2_ITERATIONS,
    saltB64: bytesToB64(salt),
    cipher: 'AES-GCM',
    ivB64: bytesToB64(iv),
    payloadB64: bytesToB64(new Uint8Array(encrypted))
  };
  return JSON.stringify(envelope, null, 2);
}

export async function decryptEncryptedBackup(raw: string, passphrase: string): Promise<Uint8Array> {
  const trimmed = passphrase.trim();
  if (trimmed.length < 8) {
    throw new Error('Passphrase must be at least 8 characters.');
  }
  let parsed: BackupEnvelopeV1;
  try {
    parsed = JSON.parse(raw) as BackupEnvelopeV1;
  } catch {
    throw new Error('Backup file is not valid JSON.');
  }
  if ((parsed?.format !== BACKUP_FORMAT && parsed?.format !== LEGACY_BACKUP_FORMAT) || parsed?.version !== BACKUP_FORMAT_VERSION) {
    throw new Error('Unsupported backup format.');
  }
  if (parsed.kdf !== 'PBKDF2-SHA256' || parsed.cipher !== 'AES-GCM') {
    throw new Error('Unsupported backup encryption parameters.');
  }
  const iterations = normalizeIterations(parsed.iterations);
  const salt = b64ToBytes(parsed.saltB64);
  const iv = b64ToBytes(parsed.ivB64);
  const ciphertext = b64ToBytes(parsed.payloadB64);
  const key = await deriveAesKey(trimmed, salt, iterations);
  try {
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toOwnedArrayBuffer(iv) },
      key,
      toOwnedArrayBuffer(ciphertext)
    );
    return new Uint8Array(decrypted);
  } catch {
    throw new Error('Wrong passphrase or corrupted backup file.');
  }
}

async function deriveAesKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: toOwnedArrayBuffer(salt), iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function normalizeIterations(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error('Backup file has invalid key-derivation settings.');
  }
  const normalized = Math.trunc(parsed);
  if (normalized < PBKDF2_ITERATIONS_MIN || normalized > PBKDF2_ITERATIONS_MAX) {
    throw new Error('Backup file uses unsupported key-derivation settings.');
  }
  return normalized;
}

function toOwnedArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return Uint8Array.from(bytes).buffer;
}

function bytesToB64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function b64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}
